import React, { useState, useEffect } from 'react';
import { getKPIData } from '../api';

function KPIDashboard() {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('week');
  // Get tenant_id from logged-in user
const user = JSON.parse(localStorage.getItem('user') || '{}');
const tenantId = user.tenant_id || 1;

  const getDateRange = (range) => {
    const end = new Date();
    const start = new Date();
    
    switch (range) {
      case 'week':
        start.setDate(end.getDate() - 7);
        break;
      case 'month':
        start.setMonth(end.getMonth() - 1);
        break;
      case 'year':
        start.setFullYear(end.getFullYear() - 1);
        break;
      default:
        start.setDate(end.getDate() - 7);
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const fetchKPIData = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange(dateRange);
      const response = await getKPIData(tenantId, start, end);
      setKpiData(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load KPI data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKPIData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const fixedCosts = { week: 0, month: 0, year: 0 };

  const getFixedCostForPeriod = () => fixedCosts[dateRange] || 0;

  const calculateProfit = () => {
    if (!kpiData) return 0;
    const revenue = parseFloat(kpiData.total_revenue) || 0;
    const lotCosts = parseFloat(kpiData.total_lot_costs) || 0;
    const laborCost = parseFloat(kpiData.total_labor_cost) || 0;
    const fixedCost = getFixedCostForPeriod();
    return revenue - lotCosts - laborCost - fixedCost;
  };

  const calculateMargin = () => {
    if (!kpiData) return 0;
    const revenue = parseFloat(kpiData.total_revenue) || 0;
    if (revenue === 0) return 0;
    return (calculateProfit() / revenue) * 100;
  };

  const formatCurrency = (value) => '$' + (parseFloat(value) || 0).toFixed(2);
  const formatNumber = (value) => parseInt(value) || 0;

  const getTotalCosts = () => {
    const lotCosts = parseFloat(kpiData?.total_lot_costs) || 0;
    const laborCost = parseFloat(kpiData?.total_labor_cost) || 0;
    return lotCosts + laborCost + getFixedCostForPeriod();
  };

  const getPeriodLabel = () => {
    switch (dateRange) {
      case 'week': return 'last 7 days';
      case 'month': return 'last 30 days';
      case 'year': return 'last 365 days';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '48px' }}>
        <p className="text-gray-600">Loading KPI data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '48px', paddingBottom: '40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">KPI Dashboard</h1>
          <p className="text-gray-600 mt-1">Performance metrics and profitability analysis</p>
        </div>
        <div style={{
          display: 'flex',
          gap: '8px',
          backgroundColor: '#ffffff',
          padding: '4px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          {['week', 'month', 'year'].map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: dateRange === range ? '#3b82f6' : 'transparent',
                color: dateRange === range ? '#ffffff' : '#374151',
                fontWeight: '500',
                textTransform: 'capitalize'
              }}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Main KPIs - 4 cards in one row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '20px' }}>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Total Cars</p>
          <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>{formatNumber(kpiData?.total_cars)}</p>
        </div>
        <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '20px' }}>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Total Revenue</p>
          <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#16a34a' }}>{formatCurrency(kpiData?.total_revenue)}</p>
        </div>
        <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '20px' }}>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Profit</p>
          <p style={{ fontSize: '28px', fontWeight: 'bold', color: calculateProfit() >= 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(calculateProfit())}</p>
        </div>
        <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '20px' }}>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Margin</p>
          <p style={{ fontSize: '28px', fontWeight: 'bold', color: calculateMargin() >= 0 ? '#16a34a' : '#dc2626' }}>{calculateMargin().toFixed(1)}%</p>
        </div>
      </div>

      {/* Bottom 4 cards - 2x2 grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
        {/* Revenue */}
        <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Revenue</h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Total Revenue</span>
            <span style={{ fontWeight: '600', color: '#16a34a' }}>{formatCurrency(kpiData?.total_revenue)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Total Shifts</span>
            <span style={{ fontWeight: '600' }}>{formatNumber(kpiData?.total_shifts)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6b7280' }}>Avg Revenue per Shift</span>
            <span style={{ fontWeight: '600' }}>{formatCurrency((parseFloat(kpiData?.total_revenue) || 0) / (parseInt(kpiData?.total_shifts) || 1))}</span>
          </div>
        </div>

        {/* Costs */}
        <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Costs</h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Labor Cost</span>
            <span style={{ fontWeight: '600', color: '#dc2626' }}>{formatCurrency(kpiData?.total_labor_cost)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Lot Costs (Paid Cars)</span>
            <span style={{ fontWeight: '600', color: '#dc2626' }}>{formatCurrency(kpiData?.total_lot_costs)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Fixed Costs</span>
            <span style={{ fontWeight: '600', color: '#dc2626' }}>{formatCurrency(getFixedCostForPeriod())}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '4px' }}>
            <span style={{ fontWeight: '600', color: '#111827' }}>Total Costs</span>
            <span style={{ fontWeight: '700', color: '#dc2626' }}>{formatCurrency(getTotalCosts())}</span>
          </div>
        </div>

        {/* Cars */}
        <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Cars</h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Total Cars</span>
            <span style={{ fontWeight: '600' }}>{formatNumber(kpiData?.total_cars)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Free Cars</span>
            <span style={{ fontWeight: '600' }}>{formatNumber(kpiData?.total_free_cars)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Paid Cars</span>
            <span style={{ fontWeight: '600' }}>{formatNumber(kpiData?.total_paid_cars)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6b7280' }}>Avg Revenue per Car</span>
            <span style={{ fontWeight: '600' }}>{formatCurrency((parseFloat(kpiData?.total_revenue) || 0) / (parseInt(kpiData?.total_cars) || 1))}</span>
          </div>
        </div>

        {/* Performance Summary */}
        <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Performance Summary</h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Revenue</span>
            <span style={{ fontWeight: '600', color: '#16a34a' }}>+{formatCurrency(kpiData?.total_revenue)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Labor Cost</span>
            <span style={{ fontWeight: '600', color: '#dc2626' }}>-{formatCurrency(kpiData?.total_labor_cost)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Lot Costs</span>
            <span style={{ fontWeight: '600', color: '#dc2626' }}>-{formatCurrency(kpiData?.total_lot_costs)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280' }}>Fixed Costs</span>
            <span style={{ fontWeight: '600', color: '#dc2626' }}>-{formatCurrency(getFixedCostForPeriod())}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '4px' }}>
            <span style={{ fontWeight: '700', color: '#111827' }}>Net Profit</span>
            <span style={{ fontWeight: '700', fontSize: '18px', color: calculateProfit() >= 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(calculateProfit())}</span>
          </div>
        </div>
      </div>

      {/* Period Info */}
      <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px', marginTop: '24px' }}>
        Showing data for the {getPeriodLabel()}
      </p>
    </div>
  );
}

export default KPIDashboard;